import { ViewerManager } from '@components'
import { AutoSaveIndicator } from '@components/AutoSaveIndicator'
import PreciosHubProvider from '@context/PreciosHubContext'
import { executeNewSession } from '@context/user'
import useUpdatedPrefs from '@hooks/useUpdatedPrefs'
import { AutenticateProxy, setRememberSession } from '@proxies/auth'
import { useAppStore } from '@store'
import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { PageTracker } from '../PageTracker'

export function Planner() {
  const setPrefs = useAppStore.use.setPrefs()
  const navigate = useNavigate()
  const location = useLocation()
  const hasNavigatedRef = useRef(false)
  const [localName, setLocalName] = useState('')
  const prevPathRef = useRef(location.pathname)
  const [isInitialized, setIsInitialized] = useState(false)

  const isUserLogged = () => window.localStorage.getItem('isUserLogged') === 'true'

  // Ejecutar newSession cuando se monta el componente Planner
  useEffect(() => {
    if (!isInitialized) {
      executeNewSession().then(() => {
        setIsInitialized(true)
      })
    }
  }, [isInitialized])

  useEffect(() => {
    const userData = JSON.parse(window.localStorage.getItem('userData')) || {
      nombre: 'Public',
      apellidos: '',
      direccion: '',
      codigoPostal: null,
      telefono: '',
      idIdioma: 1,
      empresa: '',
      cif: '',
      ciudad: '',
      pais: '',
      correo: 'mgblanco1001@gmail.com',
      esEmpresa: true,
    }
    window.localStorage.setItem('userData', JSON.stringify(userData))
    setLocalName(userData.name || '')
  }, [])

  useUpdatedPrefs()

  useEffect(() => {
    if (isUserLogged()) {
      return
    }

    const allowedOrigins = ['http://localhost:9000', 'http://localhost:9001']
    const handleMessage = async (event) => {
      if (!allowedOrigins.includes(event.origin)) {
        // console.warn('Origen no permitido:', event.origin)
        return
      }
      if (
        event.data?.source === 'react-devtools-bridge' ||
        event.data?.source === 'react-devtools-content-script' ||
        event.data?.source === 'react-devtools-backend-manager' ||
        event.data?.source === 'react-devtools-hook'
      ) {
        return
      }

      if (event.data && typeof event.data === 'object' && event.data.source === 'app1') {
        const { tokenF2H, name } = event.data

        const authProxy = new AutenticateProxy()
        const authResponse = await authProxy.authenticateWithTokenF2H(tokenF2H)

        if (authResponse.token) {
          sessionStorage.removeItem('access_token')
          sessionStorage.setItem('access_token', authResponse.token)

          if (authResponse.rememberToken) {
            setRememberSession(authResponse.rememberToken)
          }

          window.localStorage.setItem('isUserLogged', 'true')

          setPrefs(authResponse.prefs, authResponse.prefsInfo)

          const userData = JSON.parse(window.localStorage.getItem('userData')) || {}

          userData.name = name || userData.name || ''
          window.localStorage.setItem('userData', JSON.stringify(userData))

          setLocalName(name || '')
          navigate('/planner')
        } else {
          console.error('Error en la autenticación:', authResponse)
        }
      }
    }
    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [setPrefs, navigate])

  const tipoUsuarioSession = sessionStorage.getItem('tipoUsuario')
  const pendingAction = localStorage.getItem('pendingAction')
  const pendingFrom = localStorage.getItem('pendingFrom')
  const pendingSave = sessionStorage.getItem('pendingSave')
  const tipoUsuarioStore = useAppStore((s) => s.tipoUsuario)

  useEffect(() => {
    const pending = pendingAction
    const pendingFromVal = pendingFrom
    const tipoUsuario = tipoUsuarioSession || tipoUsuarioStore
    const pendingSaveVal = pendingSave
    const isInSaveProject = location.pathname === '/planner/saveProject'
    const isInLogin = location.pathname === '/planner/login'

    // Manejar acción pendiente de guardar proyecto
    if (
      pending === 'saveProject' &&
      pendingFromVal === 'guardar' &&
      tipoUsuario !== 'P' &&
      pendingSaveVal === 'true' &&
      !isInSaveProject &&
      !hasNavigatedRef.current
    ) {
      hasNavigatedRef.current = true
      navigate('/planner/saveProject')
    }

    // Manejar acción pendiente de presupuesto después de login
    if (
      pending === 'presupuestoAfterSave' &&
      pendingFromVal === 'presupuesto' &&
      tipoUsuario !== 'P' &&
      pendingSaveVal === 'true' &&
      !isInSaveProject &&
      !isInLogin &&
      !hasNavigatedRef.current
    ) {
      hasNavigatedRef.current = true
      navigate('/planner/saveProject')
    }

    if ((!pending && !pendingFromVal && !pendingSaveVal) || !isInSaveProject) {
      hasNavigatedRef.current = false
    }
  }, [
    navigate,
    tipoUsuarioSession,
    pendingAction,
    pendingFrom,
    pendingSave,
    tipoUsuarioStore,
    location.pathname,
  ])

  useEffect(() => {
    const isInSaveProject = location.pathname === '/planner/saveProject'
    const wasInSaveProject = prevPathRef.current === '/planner/saveProject'
    const pending = localStorage.getItem('pendingAction')
    const pendingFrom = localStorage.getItem('pendingFrom')
    const pendingSave = sessionStorage.getItem('pendingSave')
    const tipoUsuario = sessionStorage.getItem('tipoUsuario') || useAppStore.getState().tipoUsuario
    if (
      !isInSaveProject &&
      wasInSaveProject &&
      tipoUsuario !== 'P' &&
      (pending || pendingFrom || pendingSave)
    ) {
      sessionStorage.removeItem('pendingSave')
      localStorage.removeItem('pendingAction')
      localStorage.removeItem('pendingFrom')
    }
    prevPathRef.current = location.pathname
  }, [location.pathname, tipoUsuarioSession])

  // Misma imagen de arranque que index.html (sin texto "Cargando...").
  if (!isInitialized) {
    return (
      <main>
        <img
          className='h-screen w-screen max-w-none object-fill'
          src='/ImagenInicialMini.png'
          alt=''
        />
      </main>
    )
  }

  return (
    <main>
      <PageTracker />
      <PreciosHubProvider>
        <ViewerManager />
        <Outlet />
        <AutoSaveIndicator />
      </PreciosHubProvider>
    </main>
  )
}
